import React, { useState, useEffect } from 'react';
import BBALoader from '../../components/BBALoader';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, updateDoc, deleteDoc, query, where, getDocs, collection } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { fetchPortalUsersLinkedToAccounts } from '../../services/accountTeamMembersService';
import HiringAvatarCluster from '../../components/hr/HiringAvatarCluster';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaRegCircle,
  FaClock,
  FaExclamationTriangle,
  FaDollarSign,
  FaUsers,
  FaEdit,
  FaSave,
  FaTimes,
  FaPlus,
  FaStar,
  FaDownload,
  FaHeart,
  FaUpload,
  FaExternalLinkAlt,
  FaTrash
} from 'react-icons/fa';
import PortalBackground from '../../components/PortalBackground';

const PositionRequestOverview = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  // Client account + linked portal team members, for the "Requested By" card avatars.
  const [requestAccount, setRequestAccount] = useState(null);
  const [accountTeam, setAccountTeam] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [activeTab, setActiveTab] = useState('timeline');
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [showAddCandidateModal, setShowAddCandidateModal] = useState(false);
  const [newCandidate, setNewCandidate] = useState({
    name: '',
    status: 'applied',
    experience: '',
    score: '',
    appliedDate: '',
    nextStep: ''
  });
  const [candidates, setCandidates] = useState([]);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateRatings, setCandidateRatings] = useState({
    overall: 0,
    skill: 0,
    knowledge: 0,
    experience: 0,
    requirements: 0,
    culture: 0
  });
  const [timelineSteps, setTimelineSteps] = useState({});
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);
  const [showEditDescriptionModal, setShowEditDescriptionModal] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [stepCompletionDate, setStepCompletionDate] = useState('');
  const [candidateProcessSteps, setCandidateProcessSteps] = useState({});
  const [showCandidateProcessModal, setShowCandidateProcessModal] = useState(false);
  const [selectedCandidateStep, setSelectedCandidateStep] = useState(null);
  const [candidateStepCompletionDate, setCandidateStepCompletionDate] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [newBudget, setNewBudget] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(false);
  const [candidateEditData, setCandidateEditData] = useState({});
  const [resumeFile, setResumeFile] = useState(null);
  const [favoriteCandidates, setFavoriteCandidates] = useState(new Set());
  const [uploadingResume, setUploadingResume] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackNote, setFeedbackNote] = useState('');
  const [selectedAction, setSelectedAction] = useState(null);
  const [hasUnsavedStatusChange, setHasUnsavedStatusChange] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [isSavingStatus, setIsSavingStatus] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showStatusDropdown && !event.target.closest('.status-dropdown-container')) {
        setShowStatusDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusDropdown]);

  // Fetch position request data
  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const requestRef = doc(db, 'positionRequests', requestId);
        const requestDoc = await getDoc(requestRef);
        
        if (requestDoc.exists()) {
          const data = requestDoc.data();
          
          // Get user display name and company if userId exists
          let requestedByName = 'Unknown User';
          let companyName = 'Unknown Company';
          let userEmail = 'Unknown Email';
          let userPhone = 'Unknown Phone';
          let userLocation = 'Unknown Location';
          // Main contact of the requesting company (from the account's contact record).
          let mainContactName = '';
          let mainContactEmail = '';
          let mainContactPhone = '';

          if (data.userId) {
            try {
              const userDocRef = doc(db, 'users', data.userId);
              const userDoc = await getDoc(userDocRef);
              if (userDoc.exists()) {
                const userData = userDoc.data();
                requestedByName = userData.displayName ||
                                (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : '') ||
                                userData.email ||
                                'Unknown User';
                companyName = userData.company || 'Unknown Company';
                userEmail = userData.email || 'Unknown Email';
                userPhone = userData.phone || 'Unknown Phone';
                userLocation = userData.location || 'Unknown Location';

                // Resolve the company's account so we can show its main contact.
                const accountId = userData.accountId || userData.clientId;
                if (accountId) {
                  try {
                    const accountDoc = await getDoc(doc(db, 'accounts', accountId));
                    if (accountDoc.exists()) {
                      const acc = accountDoc.data();
                      companyName = acc.company || acc.companyName || acc.name || companyName;
                      const contact = acc.contact || {};
                      mainContactName =
                        contact.name ||
                        `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
                      mainContactEmail = contact.email || '';
                      mainContactPhone = contact.phone || '';
                    }
                  } catch (accountError) {
                    console.error('Error fetching account data:', accountError);
                  }
                }
              }
            } catch (error) {
              console.error('Error fetching user data:', error);
            }
          }

          // Fall back to the requester when the account has no dedicated contact record.
          if (!mainContactName) mainContactName = requestedByName;
          if (!mainContactEmail) mainContactEmail = userEmail !== 'Unknown Email' ? userEmail : '';
          if (!mainContactPhone) mainContactPhone = userPhone !== 'Unknown Phone' ? userPhone : '';

          const requestData = {
            id: requestDoc.id,
            ...data,
            status: data.status || 'pending',
            requestDate: data.createdAt || new Date().toISOString(),
            requestedByName,
            companyName,
            userEmail,
            userPhone,
            userLocation,
            mainContactName,
            mainContactEmail,
            mainContactPhone
          };
          
          setRequest(requestData);
          setEditData(requestData);
          
          // Load existing notes from request data
          setNotes(data.notes || []);
          
          // Load existing candidates from request data
          const candidatesData = data.candidates || [];
          setCandidates(candidatesData);
          
          // Load favorites from candidates data
          const favorites = new Set();
          candidatesData.forEach(candidate => {
            if (candidate.isFavorite) {
              favorites.add(candidate.id);
            }
          });
          setFavoriteCandidates(favorites);
          
          // Load existing timeline steps from request data
          setTimelineSteps(data.timelineSteps || {});
        } else {
          console.error('Request not found');
          navigate('/crm/fulfillment/hr');
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching request:', error);
        setLoading(false);
      }
    };

    if (requestId) {
      fetchRequest();
    }
  }, [requestId, navigate]);

  // Resolve the client account + its portal team members so the "Requested By"
  // card can show the same avatar cluster used on the HR Fulfillment board.
  useEffect(() => {
    if (!request) return;
    let cancelled = false;
    (async () => {
      let acct = null;
      try {
        let accountId = request.accountId || null;
        if (!accountId && request.userId) {
          const userDoc = await getDoc(doc(db, 'users', request.userId));
          if (userDoc.exists()) {
            const ud = userDoc.data();
            accountId = ud.accountId || ud.clientId || null;
          }
        }
        if (accountId) {
          const accSnap = await getDoc(doc(db, 'accounts', accountId));
          if (accSnap.exists()) acct = { id: accSnap.id, ...accSnap.data() };
        }
      } catch (err) {
        console.error('Error resolving request account:', err);
      }
      if (cancelled) return;
      setRequestAccount(acct);
      if (acct) {
        try {
          const map = await fetchPortalUsersLinkedToAccounts([acct]);
          if (!cancelled) setAccountTeam(map.get(acct.id) || []);
        } catch (err) {
          console.error('Error loading team for request account:', err);
        }
      } else {
        setAccountTeam([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [request?.id, request?.accountId, request?.userId]);

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { bg: 'bg-yellow-100 text-yellow-800', icon: <FaClock className="w-4 h-4" />, text: 'Pending' },
      'approved': { bg: 'bg-green-100 text-green-800', icon: <FaCheckCircle className="w-4 h-4" />, text: 'Approved' },
      'in-progress': { bg: 'bg-blue-100 text-blue-800', icon: <FaUsers className="w-4 h-4" />, text: 'In Progress' },
      'completed': { bg: 'bg-purple-100 text-purple-800', icon: <FaCheckCircle className="w-4 h-4" />, text: 'Completed' },
      'cancelled': { bg: 'bg-red-100 text-red-800', icon: <FaExclamationTriangle className="w-4 h-4" />, text: 'Cancelled' },
      'denied': { bg: 'bg-red-100 text-red-800', icon: <FaExclamationTriangle className="w-4 h-4" />, text: 'Denied' }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg}`}>
        {config.icon}
        <span className="ml-2">{config.text}</span>
      </span>
    );
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle status updates
  const handleStatusUpdate = (newStatus) => {
    // Set pending status and show save button
    setPendingStatus(newStatus);
    setHasUnsavedStatusChange(true);
    setShowStatusDropdown(false);
  };

  // Handle saving status changes
  const handleSaveStatusChange = async () => {
    if (!pendingStatus) return;

    setIsSavingStatus(true);
    
    try {
      console.log('Saving status change:', pendingStatus);
      console.log('Request ID:', requestId);
      console.log('Request data:', request);
      
      const requestRef = doc(db, 'positionRequests', requestId);
      
      // Update position request status
      await updateDoc(requestRef, {
        status: pendingStatus,
        updatedAt: new Date().toISOString()
      });
      
      console.log('Updated position request status');
      
      // Update corresponding position in positions collection
      // Find the position by querying for positionRequestId
      const positionsQuery = query(
        collection(db, 'positions'),
        where('positionRequestId', '==', requestId)
      );
      
      const positionsSnapshot = await getDocs(positionsQuery);
      
      if (!positionsSnapshot.empty) {
        const positionDoc = positionsSnapshot.docs[0];
        console.log('Found position with ID:', positionDoc.id);
        
        const positionStatus = pendingStatus === 'pending' ? 'Pending' :
                              pendingStatus === 'in-progress' ? 'In Progress' :
                              pendingStatus === 'completed' ? 'Completed' : 'Pending';
        
        console.log('Position status to set:', positionStatus);
        
        await updateDoc(doc(db, 'positions', positionDoc.id), {
          status: positionStatus,
          updatedAt: new Date().toISOString()
        });
        
        console.log('Updated position status');
      } else {
        console.log('No position found with positionRequestId:', requestId);
      }
      
      // Update local state
      setRequest(prev => ({ ...prev, status: pendingStatus }));
      setHasUnsavedStatusChange(false);
      setPendingStatus(null);
      
      console.log('Status change saved successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status. Please try again.');
    } finally {
      setIsSavingStatus(false);
    }
  };

  // Handle canceling status changes
  const handleCancelStatusChange = () => {
    setHasUnsavedStatusChange(false);
    setPendingStatus(null);
  };

  // Handle budget update
  const handleBudgetUpdate = async () => {
    if (!newBudget.trim()) return;

    try {
      const requestRef = doc(db, 'positionRequests', requestId);
      
      await updateDoc(requestRef, {
        budget: newBudget,
        updatedAt: new Date().toISOString()
      });
      
      // Update local state
      setRequest(prev => ({ ...prev, budget: newBudget }));
      setShowBudgetModal(false);
      setNewBudget('');
    } catch (error) {
      console.error('Error updating budget:', error);
    }
  };

  // Handle delete request
  const handleDeleteRequest = async () => {
    try {
      // Delete the linked position(s) first so the client's hiring hub doesn't
      // keep an orphaned position pointing at a deleted request.
      const positionsQuery = query(
        collection(db, 'positions'),
        where('positionRequestId', '==', requestId)
      );
      const positionsSnapshot = await getDocs(positionsQuery);
      await Promise.all(
        positionsSnapshot.docs.map((positionDoc) => deleteDoc(doc(db, 'positions', positionDoc.id)))
      );

      const requestRef = doc(db, 'positionRequests', requestId);
      await deleteDoc(requestRef);

      // Navigate back to HR fulfillment page
      navigate('/crm/fulfillment/hr');
    } catch (error) {
      console.error('Error deleting request:', error);
      alert('Error deleting request. Please try again.');
    }
  };

  // Handle edit save
  const handleSave = async () => {
    try {
      const requestRef = doc(db, 'positionRequests', requestId);

      await updateDoc(requestRef, {
        ...editData,
        updatedAt: new Date().toISOString()
      });

      // Keep the linked position(s) title in sync so the client's portal
      // hiring hub shows the same position title.
      if (editData.position && editData.position !== request.position) {
        try {
          const positionsQuery = query(
            collection(db, 'positions'),
            where('positionRequestId', '==', requestId)
          );
          const positionsSnapshot = await getDocs(positionsQuery);
          await Promise.all(
            positionsSnapshot.docs.map((positionDoc) =>
              updateDoc(doc(db, 'positions', positionDoc.id), {
                title: editData.position,
                updatedAt: new Date().toISOString()
              })
            )
          );
        } catch (syncError) {
          console.error('Error syncing position title:', syncError);
        }
      }

      setRequest(editData);
      setEditing(false);
    } catch (error) {
      console.error('Error updating request:', error);
    }
  };

  // Handle edit cancel
  const handleCancel = () => {
    setEditData(request);
    setEditing(false);
  };

  // Handle edit description
  const handleEditDescription = () => {
    setEditDescription(request?.requirements || request?.description || '');
    setShowEditDescriptionModal(true);
  };

  const handleSaveDescription = async () => {
    try {
      const requestRef = doc(db, 'positionRequests', requestId);
      await updateDoc(requestRef, {
        requirements: editDescription,
        updatedAt: new Date().toISOString()
      });
      
      setRequest(prev => ({
        ...prev,
        requirements: editDescription
      }));
      
      setShowEditDescriptionModal(false);
    } catch (error) {
      console.error('Error updating description:', error);
    }
  };

  const handleCancelEditDescription = () => {
    setEditDescription('');
    setShowEditDescriptionModal(false);
  };

  // Save notes to Firestore
  const saveNotesToFirestore = async (updatedNotes) => {
    if (!requestId) return;

    try {
      const requestRef = doc(db, 'positionRequests', requestId);
      await updateDoc(requestRef, {
        notes: updatedNotes,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving notes to Firestore:', error);
    }
  };

  // Handle add note
  const handleAddNote = async () => {
    if (!newNote.trim()) {
      return; // Don't add empty notes
    }

    const currentDate = new Date();
    const formattedDate = `${currentDate.getMonth() + 1}/${currentDate.getDate()} ${currentDate.getHours() > 12 ? currentDate.getHours() - 12 : currentDate.getHours()}:${currentDate.getMinutes().toString().padStart(2, '0')}${currentDate.getHours() >= 12 ? 'PM' : 'AM'}`;

    // Get user display name from profile data
    const getUserDisplayName = async () => {
      if (!currentUser) return 'Current User';
      
      try {
        // Try to get from internal_users collection first (CRM users)
        const internalUserDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
        if (internalUserDoc.exists()) {
          const internalUserData = internalUserDoc.data();
          const fullName = `${internalUserData.firstName || ''} ${internalUserData.lastName || ''}`.trim();
          if (fullName) {
            return fullName;
          }
        }
        
        // If not found, try users collection (portal users)
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
          if (fullName) {
            return fullName;
          }
        }
        
        // Fallback to display name
        if (currentUser.displayName) {
          return currentUser.displayName;
        }
        
        // Last fallback: extract name from email
        if (currentUser.email) {
          const emailName = currentUser.email.split('@')[0];
          return emailName
            .replace(/[._-]/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        }
        
        return 'Current User';
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // Fallback to display name or email
        return currentUser.displayName || currentUser.email?.split('@')[0] || 'Current User';
      }
    };

    // Get user profile image
    const getUserProfileImage = async () => {
      if (!currentUser) return null;
      
      try {
        // Try to get from internal_users collection first (CRM users)
        const internalUserDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
        if (internalUserDoc.exists()) {
          const internalUserData = internalUserDoc.data();
          if (internalUserData.profileImage) {
            return internalUserData.profileImage;
          }
        }
        
        // If not found, try users collection (portal users)
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.profileImage) {
            return userData.profileImage;
          }
        }
        
        return currentUser.photoURL || null;
      } catch (error) {
        console.error('Error fetching user profile image:', error);
        return currentUser.photoURL || null;
      }
    };

    const displayName = await getUserDisplayName();
    const profileImage = await getUserProfileImage();

    const newNoteItem = {
      id: Date.now(),
      content: newNote,
      author: {
        name: displayName,
        photoURL: profileImage,
        uid: currentUser?.uid || null
      },
      timestamp: new Date().toISOString(),
      date: formattedDate
    };

    const updatedNotes = [newNoteItem, ...notes]; // Add new note at the beginning
    setNotes(updatedNotes);
    setNewNote('');
    setShowAddNoteModal(false);
    
    // Save to Firestore
    await saveNotesToFirestore(updatedNotes);
  };

  // Handle delete note
  const handleDeleteNote = async (noteId) => {
    const updatedNotes = notes.filter(note => note.id !== noteId);
    setNotes(updatedNotes);

    // Save to Firestore
    await saveNotesToFirestore(updatedNotes);
  };

  // Begin editing a note's text
  const handleStartEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.content || '');
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  // Save an edited note
  const handleSaveEditNote = async (noteId) => {
    if (!editingNoteText.trim()) return;

    const updatedNotes = notes.map((note) =>
      note.id === noteId
        ? { ...note, content: editingNoteText.trim(), editedAt: new Date().toISOString() }
        : note
    );
    setNotes(updatedNotes);
    setEditingNoteId(null);
    setEditingNoteText('');

    await saveNotesToFirestore(updatedNotes);
  };

  // Save candidates to Firestore
  const saveCandidatesToFirestore = async (updatedCandidates) => {
    if (!requestId) return;

    try {
      const requestRef = doc(db, 'positionRequests', requestId);
      await updateDoc(requestRef, {
        candidates: updatedCandidates,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving candidates to Firestore:', error);
    }
  };

  // Save timeline steps to Firestore
  const saveTimelineStepsToFirestore = async (updatedTimelineSteps) => {
    if (!requestId) return;

    try {
      const requestRef = doc(db, 'positionRequests', requestId);
      await updateDoc(requestRef, {
        timelineSteps: updatedTimelineSteps,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving timeline steps to Firestore:', error);
    }
  };

  // Handle timeline step completion
  const handleTimelineStepClick = (stepId) => {
    const stepData = timelineSteps[stepId];
    const isCompleted = stepData?.completed;
    
    if (isCompleted) {
      // If already completed, mark as incomplete directly
      handleMarkStepIncomplete(stepId);
    } else {
      // If not completed, show modal to mark as complete
      setSelectedStep(stepId);
      setStepCompletionDate('');
      setShowTimelineModal(true);
    }
  };

  // Handle marking timeline step as complete
  const handleMarkStepComplete = async () => {
    if (!selectedStep || !stepCompletionDate) {
      return;
    }

    const updatedTimelineSteps = {
      ...timelineSteps,
      [selectedStep]: {
        completed: true,
        completedDate: stepCompletionDate,
        completedAt: new Date().toISOString()
      }
    };

    setTimelineSteps(updatedTimelineSteps);
    setShowTimelineModal(false);
    setSelectedStep(null);
    setStepCompletionDate('');

    // Save to Firestore
    await saveTimelineStepsToFirestore(updatedTimelineSteps);
  };

  // Handle marking timeline step as incomplete
  const handleMarkStepIncomplete = async (stepId) => {
    const updatedTimelineSteps = {
      ...timelineSteps,
      [stepId]: {
        completed: false,
        completedDate: null,
        completedAt: null
      }
    };

    setTimelineSteps(updatedTimelineSteps);

    // Save to Firestore
    await saveTimelineStepsToFirestore(updatedTimelineSteps);
  };

  // Save candidate process steps to Firestore
  const saveCandidateProcessStepsToFirestore = async (candidateId, updatedProcessSteps) => {
    if (!requestId || !candidateId) return;

    try {
      const requestRef = doc(db, 'positionRequests', requestId);
      const updatedCandidates = candidates.map(candidate => 
        candidate.id === candidateId 
          ? { ...candidate, processSteps: updatedProcessSteps }
          : candidate
      );
      
      await updateDoc(requestRef, {
        candidates: updatedCandidates,
        updatedAt: new Date().toISOString()
      });
      
      // Update local state
      setCandidates(updatedCandidates);
    } catch (error) {
      console.error('Error saving candidate process steps to Firestore:', error);
    }
  };

  // Handle candidate process step click
  const handleCandidateProcessStepClick = (stepId) => {
    const stepData = candidateProcessSteps[stepId];
    const isCompleted = stepData?.completed;
    
    if (isCompleted) {
      // If already completed, mark as incomplete directly
      handleMarkCandidateStepIncomplete(stepId);
    } else {
      // If not completed, show modal to mark as complete
      setSelectedCandidateStep(stepId);
      setCandidateStepCompletionDate('');
      setShowCandidateProcessModal(true);
    }
  };

  // Handle marking candidate process step as complete
  const handleMarkCandidateStepComplete = async () => {
    if (!selectedCandidateStep || !candidateStepCompletionDate || !selectedCandidate) {
      return;
    }

    const updatedProcessSteps = {
      ...candidateProcessSteps,
      [selectedCandidateStep]: {
        completed: true,
        completedDate: candidateStepCompletionDate,
        completedAt: new Date().toISOString()
      }
    };

    setCandidateProcessSteps(updatedProcessSteps);
    setShowCandidateProcessModal(false);
    setSelectedCandidateStep(null);
    setCandidateStepCompletionDate('');

    // Update selected candidate
    setSelectedCandidate(prev => ({ ...prev, processSteps: updatedProcessSteps }));

    // Save to Firestore
    await saveCandidateProcessStepsToFirestore(selectedCandidate.id, updatedProcessSteps);
  };

  // Handle marking candidate process step as incomplete
  const handleMarkCandidateStepIncomplete = async (stepId) => {
    const updatedProcessSteps = {
      ...candidateProcessSteps,
      [stepId]: {
        completed: false,
        completedDate: null,
        completedAt: null
      }
    };

    setCandidateProcessSteps(updatedProcessSteps);

    // Update selected candidate
    if (selectedCandidate) {
      setSelectedCandidate(prev => ({ ...prev, processSteps: updatedProcessSteps }));
      await saveCandidateProcessStepsToFirestore(selectedCandidate.id, updatedProcessSteps);
    }
  };

  // Handle add candidate
  const handleAddCandidate = async () => {
    if (!newCandidate.name.trim()) {
      return; // Don't add empty candidates
    }

    const candidateData = {
      id: Date.now(),
      name: newCandidate.name,
      status: newCandidate.status,
      experience: newCandidate.experience,
      score: newCandidate.score,
      appliedDate: newCandidate.appliedDate || new Date().toISOString(),
      nextStep: newCandidate.nextStep,
      createdAt: new Date().toISOString()
    };

    // Add to local state
    const updatedCandidates = [...candidates, candidateData];
    setCandidates(updatedCandidates);

    // Reset form
    setNewCandidate({
      name: '',
      status: 'applied',
      experience: '',
      score: '',
      appliedDate: '',
      nextStep: ''
    });
    setShowAddCandidateModal(false);
    
    // Save to Firestore
    await saveCandidatesToFirestore(updatedCandidates);
  };

  // Handle candidate row click
  const handleCandidateClick = (candidate) => {
    setSelectedCandidate(candidate);
    setShowCandidateModal(true);
    // Load candidate ratings if they exist
    setCandidateRatings(candidate.ratings || {
      overall: 0,
      skill: 0,
      knowledge: 0,
      experience: 0,
      requirements: 0,
      culture: 0
    });
    // Load candidate process steps if they exist
    setCandidateProcessSteps(candidate.processSteps || {});
    // Initialize edit data
    setCandidateEditData({
      name: candidate.name || '',
      appliedDate: candidate.appliedDate || '',
      email: candidate.email || '',
      phone: candidate.phone || '',
      salary: candidate.salary || '',
      startDate: candidate.startDate || '',
      additionalItems: candidate.additionalItems || ''
    });
    // Set the selected action based on candidate's current status
    if (candidate.actionStatus === 'rejected') {
      setSelectedAction('reject');
    } else if (candidate.actionStatus === 'continue') {
      setSelectedAction('continue');
    } else {
      setSelectedAction(null);
    }
  };

  // Handle feedback modal
  const handleOpenFeedback = () => {
    setFeedbackRating(selectedCandidate?.feedbackRating || 0);
    setFeedbackNote(selectedCandidate?.feedbackNote || '');
    setShowFeedbackModal(true);
  };

  const handleSaveFeedback = async () => {
    if (!selectedCandidate) return;

    const updatedCandidates = candidates.map(c => 
      c.id === selectedCandidate.id 
        ? { 
            ...c, 
            feedbackRating: feedbackRating,
            feedbackNote: feedbackNote,
            feedbackUpdatedAt: new Date().toISOString()
          }
        : c
    );
    setCandidates(updatedCandidates);
    
    // Update selected candidate
    setSelectedCandidate({
      ...selectedCandidate,
      feedbackRating: feedbackRating,
      feedbackNote: feedbackNote,
      feedbackUpdatedAt: new Date().toISOString()
    });
    
    // Save to Firestore
    await saveCandidatesToFirestore(updatedCandidates);
    
    setShowFeedbackModal(false);
  };

  const handleCancelFeedback = () => {
    setFeedbackRating(0);
    setFeedbackNote('');
    setShowFeedbackModal(false);
  };

  // Handle action selection
  const handleActionSelect = async (action) => {
    if (!selectedCandidate) return;
    
    const newSelectedAction = selectedAction === action ? null : action;
    setSelectedAction(newSelectedAction);
    
    // If an action is selected, save it to Firestore
    if (newSelectedAction) {
      let updateData = {};
      
      if (newSelectedAction === 'reject') {
        updateData = {
          isRejected: true,
          rejectedAt: new Date().toISOString(),
          actionStatus: 'rejected'
        };
      } else if (newSelectedAction === 'continue') {
        updateData = {
          isRejected: false,
          actionStatus: 'continue',
          continuedAt: new Date().toISOString()
        };
      }
      
      // Update candidates array
      const updatedCandidates = candidates.map(c => 
        c.id === selectedCandidate.id 
          ? { ...c, ...updateData }
          : c
      );
      setCandidates(updatedCandidates);
      
      // Update selected candidate
      setSelectedCandidate({
        ...selectedCandidate,
        ...updateData
      });
      
      // Save to Firestore
      await saveCandidatesToFirestore(updatedCandidates);
    }
  };

  // Handle candidate edit start
  const handleStartEditCandidate = () => {
    setEditingCandidate(true);
  };

  // Handle candidate edit save
  const handleSaveCandidateEdit = async () => {
    try {
      const updatedCandidates = candidates.map(candidate => 
        candidate.id === selectedCandidate.id 
          ? { 
              ...candidate, 
              ...candidateEditData,
              updatedAt: new Date().toISOString()
            }
          : candidate
      );
      
      setCandidates(updatedCandidates);
      setSelectedCandidate({ ...selectedCandidate, ...candidateEditData });
      setEditingCandidate(false);
      
      // Save to Firestore
      await saveCandidatesToFirestore(updatedCandidates);
    } catch (error) {
      console.error('Error updating candidate:', error);
    }
  };

  // Handle candidate edit cancel
  const handleCancelCandidateEdit = () => {
    setCandidateEditData({
      name: selectedCandidate.name || '',
      appliedDate: selectedCandidate.appliedDate || '',
      email: selectedCandidate.email || '',
      phone: selectedCandidate.phone || '',
      salary: selectedCandidate.salary || '',
      startDate: selectedCandidate.startDate || '',
      additionalItems: selectedCandidate.additionalItems || ''
    });
    setEditingCandidate(false);
    setResumeFile(null);
    setUploadingResume(false);
    setUploadProgress(0);
  };

  // Handle resume file upload
  const handleResumeUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      alert('Please select a PDF file');
      return;
    }

    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      alert('File size exceeds 50MB limit. Please select a smaller file.');
      return;
    }

    setResumeFile(file);
    setUploadingResume(true);
    setUploadProgress(0);

    try {
      // Create unique filename
      const timestamp = Date.now();
      const fileName = `${selectedCandidate.name.replace(/[^a-zA-Z0-9]/g, '_')}_Resume_${timestamp}.pdf`;
      const storageRef = ref(storage, `candidate-resumes/${requestId}/${selectedCandidate.id}/${fileName}`);
      
      setUploadProgress(20);
      
      // Upload file to Firebase Storage
      const snapshot = await uploadBytes(storageRef, file);
      setUploadProgress(60);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      setUploadProgress(80);
      
      // Update candidate with resume URL
      const updatedCandidates = candidates.map(candidate => 
        candidate.id === selectedCandidate.id 
          ? { 
              ...candidate, 
              resumeUrl: downloadURL,
              resumeFileName: fileName,
              resumeUploadDate: new Date().toISOString()
            }
          : candidate
      );
      
      setCandidates(updatedCandidates);
      setSelectedCandidate(prev => ({ 
        ...prev, 
        resumeUrl: downloadURL,
        resumeFileName: fileName,
        resumeUploadDate: new Date().toISOString()
      }));
      
      setUploadProgress(100);
      
      // Save to Firestore
      await saveCandidatesToFirestore(updatedCandidates);
      
      // Reset upload state after a short delay
      setTimeout(() => {
        setUploadingResume(false);
        setUploadProgress(0);
        setResumeFile(null);
      }, 1000);
      
    } catch (error) {
      console.error('Error uploading resume:', error);
      alert('Error uploading resume. Please try again.');
      setUploadingResume(false);
      setUploadProgress(0);
      setResumeFile(null);
    }
  };

  // Handle deleting an already-uploaded resume from a candidate
  const handleDeleteResume = async () => {
    if (!selectedCandidate?.resumeUrl) return;
    if (!window.confirm('Delete this resume? This cannot be undone.')) return;

    // Best-effort removal from Storage; the file may already be gone or the
    // path may differ for legacy uploads, so we don't block the record update.
    if (selectedCandidate.resumeFileName) {
      try {
        const storageRef = ref(
          storage,
          `candidate-resumes/${requestId}/${selectedCandidate.id}/${selectedCandidate.resumeFileName}`
        );
        await deleteObject(storageRef);
      } catch (error) {
        console.warn('Could not remove resume file from storage:', error);
      }
    }

    const updatedCandidates = candidates.map((candidate) =>
      candidate.id === selectedCandidate.id
        ? { ...candidate, resumeUrl: null, resumeFileName: null, resumeUploadDate: null }
        : candidate
    );
    setCandidates(updatedCandidates);
    setSelectedCandidate((prev) => ({ ...prev, resumeUrl: null, resumeFileName: null, resumeUploadDate: null }));
    await saveCandidatesToFirestore(updatedCandidates);
  };

  // Handle deleting a candidate entirely
  const handleDeleteCandidate = async () => {
    if (!selectedCandidate) return;
    if (!window.confirm('Delete this candidate? This cannot be undone.')) return;

    const updatedCandidates = candidates.filter((candidate) => candidate.id !== selectedCandidate.id);
    setCandidates(updatedCandidates);
    await saveCandidatesToFirestore(updatedCandidates);

    setShowCandidateModal(false);
    setSelectedCandidate(null);
    setEditingCandidate(false);
    setCandidateEditData({});
    setResumeFile(null);
  };

  // Handle favorite toggle
  const handleToggleFavorite = async (candidateId) => {
    const newFavorites = new Set(favoriteCandidates);
    if (newFavorites.has(candidateId)) {
      newFavorites.delete(candidateId);
    } else {
      newFavorites.add(candidateId);
    }
    setFavoriteCandidates(newFavorites);
    
    // Update the candidate in the candidates array. Stamp who/when so the
    // client's "What's New" feed can surface (and attribute) the favorite.
    const nowFavorited = newFavorites.has(candidateId);
    const updatedCandidates = candidates.map(candidate =>
      candidate.id === candidateId
        ? {
            ...candidate,
            isFavorite: nowFavorited,
            favoritedAt: nowFavorited ? new Date().toISOString() : null,
            favoritedBy: nowFavorited ? (currentUser?.uid || null) : null,
          }
        : candidate
    );

    setCandidates(updatedCandidates);
    
    // Update selected candidate if it's the same one
    if (selectedCandidate && selectedCandidate.id === candidateId) {
      setSelectedCandidate(prev => ({ ...prev, isFavorite: newFavorites.has(candidateId) }));
    }
    
    // Save to Firestore
    await saveCandidatesToFirestore(updatedCandidates);
  };

  // Handle star rating click
  const handleRatingClick = async (category, rating) => {
    const newRatings = {
      ...candidateRatings,
      [category]: rating
    };
    setCandidateRatings(newRatings);
    
    // Update the candidate in the candidates array
    const updatedCandidates = candidates.map(candidate => 
      candidate.id === selectedCandidate.id 
        ? { ...candidate, ratings: newRatings }
        : candidate
    );
    
    setCandidates(updatedCandidates);
    
    // Update selected candidate
    setSelectedCandidate(prev => ({ ...prev, ratings: newRatings }));
    
    // Save to Firestore
    await saveCandidatesToFirestore(updatedCandidates);
  };

  // Get candidate current stage
  const getCandidateStage = (candidate) => {
    const processSteps = candidate.processSteps || {};
    const stepOrder = ['applied', 'pre-screen-interview', 'company-interview', 'decision-offer'];
    
    for (let i = stepOrder.length - 1; i >= 0; i--) {
      const step = stepOrder[i];
      if (processSteps[step]?.completed) {
        return step.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      }
    }
    
    return 'Applied';
  };

  // Get candidate overall score
  const getCandidateScore = (candidate) => {
    const ratings = candidate.ratings || {};
    const overallScore = ratings.overall || 0;
    return `${overallScore}/5`;
  };

  // Render star rating component
  const renderStarRating = (category, currentRating) => {
    return (
      <div className="flex space-x-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => handleRatingClick(category, star)}
            className={`transition-colors duration-200 ${
              star <= currentRating 
                ? 'text-yellow-400 hover:text-yellow-500' 
                : 'text-gray-300 hover:text-yellow-400'
            }`}
          >
            <FaStar className="w-6 h-6" />
          </button>
        ))}
      </div>
    );
  };

  // Tab definitions
  const tabs = [
    { id: 'timeline', name: 'Timeline', icon: <FaClock /> },
    { id: 'candidates', name: 'Candidates', icon: <FaUsers /> },
    { id: 'notes', name: 'Notes', icon: <FaEdit /> }
  ];

  // Render timeline tab
  const renderTimelineTab = () => {
    if (!request) {
      return (
        <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <p>Loading timeline...</p>
        </div>
      );
    }

    const timelineStepData = [
      {
        id: 'define-requirements',
        title: 'Define Role Requirements',
        description: 'Establish clear job responsibilities, skills, and experience requirements',
        defaultCompleted: true,
        defaultDate: request.requestDate
      },
      {
        id: 'create-posting',
        title: 'Create Posting',
        description: 'Develop compelling job description and post to relevant platforms',
        statusDependent: false
      },
      {
        id: 'screen-applicants',
        title: 'Screen Applicants',
        description: 'Review resumes and applications to identify qualified candidates',
        statusDependent: false
      },
      {
        id: 'pre-screen-interview',
        title: 'Pre-Screen Interview',
        description: 'Initial phone or video interview to assess basic qualifications',
        statusDependent: false
      },
      {
        id: 'company-interview',
        title: 'Company Interview',
        description: 'In-person or virtual interview with team members and stakeholders',
        statusDependent: false
      },
      {
        id: 'decision-offer',
        title: 'Decision & Offer',
        description: 'Final evaluation and job offer to selected candidate',
        statusDependent: false
      }
    ];

    // The next actionable step = first one that isn't complete yet. We outline it
    // and prompt the user to check it off.
    const nextStepIndex = timelineStepData.findIndex((step) => {
      const sd = timelineSteps[step.id];
      return !(sd?.completed || (step.defaultCompleted && !sd));
    });

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hiring Timeline - Left Half */}
        <div className={`p-6 rounded-lg shadow-md border ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <h3 className={`text-lg font-semibold mb-1 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Hiring Timeline
          </h3>
          <p className={`text-xs mb-6 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Check off each step as you complete it.
          </p>
          <div className="space-y-2">
            {timelineStepData.map((step, index) => {
              const stepData = timelineSteps[step.id];
              const isCompleted = stepData?.completed || (step.defaultCompleted && !stepData);
              const completionDate = stepData?.completedDate || (step.defaultDate && !stepData ? step.defaultDate : null);
              const isClickable = (!step.statusDependent || step.requiredStatus.includes(request.status));
              const isCurrent = index === nextStepIndex;

              return (
                <div
                  key={step.id}
                  className={`flex items-start space-x-4 rounded-lg p-3 transition-colors ${
                    isCurrent
                      ? (darkMode ? 'bg-red-900/20 ring-2 ring-red-500' : 'bg-red-50 ring-2 ring-red-500')
                      : ''
                  }`}
                >
                  <div className="flex-shrink-0">
                    <button
                      type="button"
                      onClick={isClickable ? () => handleTimelineStepClick(step.id) : undefined}
                      disabled={!isClickable}
                      title={isCompleted ? 'Click to mark as incomplete' : 'Click to mark this step complete'}
                      className={`w-9 h-9 rounded-full flex items-center justify-center transition-all outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-500 ${
                        darkMode ? 'focus-visible:ring-offset-gray-800' : 'focus-visible:ring-offset-white'
                      } ${isClickable ? 'cursor-pointer' : 'cursor-default'} ${
                        isCompleted
                          ? 'text-green-500 hover:text-green-600'
                          : isCurrent
                          ? (darkMode ? 'text-red-400 hover:text-green-500' : 'text-red-500 hover:text-green-600')
                          : (darkMode ? 'text-gray-500 hover:text-green-500' : 'text-gray-400 hover:text-green-600')
                      }`}
                    >
                      {isCompleted ? (
                        <FaCheckCircle className="w-7 h-7" />
                      ) : (
                        <FaRegCircle className="w-7 h-7" />
                      )}
                    </button>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-2 mb-1">
                      <span className={`text-sm font-medium ${
                        isCompleted
                          ? (darkMode ? 'text-green-200' : 'text-green-800')
                          : isCurrent
                          ? (darkMode ? 'text-red-300' : 'text-red-700')
                          : (darkMode ? 'text-white' : 'text-gray-900')
                      }`}>
                        {step.title}
                      </span>
                      {isCurrent && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-600 text-white">
                          Next — check off when done
                        </span>
                      )}
                      {isCompleted && completionDate && (
                        <span className={`text-xs ${darkMode ? 'text-green-300' : 'text-green-600'}`}>
                          {formatDate(completionDate)}
                        </span>
                      )}
                      {isCompleted && stepData && (
                        <button
                          onClick={() => handleMarkStepIncomplete(step.id)}
                          className={`text-xs px-2 py-1 rounded ${
                            darkMode
                              ? 'text-red-300 hover:bg-red-900'
                              : 'text-red-600 hover:bg-red-100'
                          } transition-colors`}
                          title="Mark as incomplete"
                        >
                          Undo
                        </button>
                      )}
                    </div>
                    <p className={`text-sm ${
                      isCompleted
                        ? (darkMode ? 'text-green-200' : 'text-green-700')
                        : (darkMode ? 'text-gray-400' : 'text-gray-600')
                    }`}>
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Posting Requirements - Right Half */}
        <div className={`p-6 rounded-lg shadow-md border ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex justify-between items-center mb-6">
            <div className="flex-1"></div>
            <h3 className={`text-lg font-semibold text-center flex-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Posting Requirements
            </h3>
            <div className="flex-1 flex justify-end">
              <button
                onClick={handleEditDescription}
                className={`flex items-center px-3 py-1 text-sm rounded-md transition-colors ${
                  darkMode 
                    ? 'text-gray-300 hover:text-white hover:bg-gray-700' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <FaEdit className="w-4 h-4 mr-1" />
                Edit
              </button>
            </div>
          </div>
          
          <div className={`p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {request.requirements || request.description || 'No specific requirements provided.'}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Render candidates tab
  const renderCandidatesTab = () => (
    <div className="space-y-6">
      <div className={`overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div className="flex-1"></div>
          <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
            Candidates
          </h3>
          <div className="flex-1 flex justify-end">
            <button
              onClick={() => setShowAddCandidateModal(true)}
              className={`p-2 text-white ${darkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-600 hover:bg-red-700'} rounded-full transition-colors duration-200`}
              title="Add Candidate"
            >
              <FaPlus className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                  Candidate
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                  Feedback
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                  Stage
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                  Action
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                  Score
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                  Applied
                </th>
              </tr>
            </thead>
            <tbody className={`${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
              {candidates.length > 0 ? (
                candidates.map((candidate) => (
                  <tr 
                    key={candidate.id} 
                    className={`${candidate.isFavorite ? (darkMode ? 'bg-yellow-900/20' : 'bg-yellow-50') : ''} ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} cursor-pointer transition-colors duration-200`}
                    onClick={() => handleCandidateClick(candidate)}
                  >
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {candidate.name}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {candidate.feedbackRating ? (
                        <div className="flex items-center">
                          <div className="flex mr-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <FaStar 
                                key={star}
                                className={`w-3 h-3 ${
                                  star <= candidate.feedbackRating
                                    ? 'text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-xs">
                            {candidate.feedbackRating}/5
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">No rating</span>
                      )}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        getCandidateStage(candidate) === 'Decision Offer' ? 'bg-green-100 text-green-800' :
                        getCandidateStage(candidate) === 'Company Interview' ? 'bg-blue-100 text-blue-800' :
                        getCandidateStage(candidate) === 'Pre Screen Interview' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {getCandidateStage(candidate)}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {candidate.actionStatus ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          candidate.actionStatus === 'rejected' 
                            ? 'bg-red-100 text-red-800' 
                            : candidate.actionStatus === 'continue'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {candidate.actionStatus === 'rejected' ? 'Rejected' : 
                           candidate.actionStatus === 'continue' ? 'Continue' : 
                           candidate.actionStatus}
                        </span>
                      ) : (
                        <span className="text-gray-400">No action</span>
                      )}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <div className="flex items-center">
                        <span className="mr-2">{getCandidateScore(candidate)}</span>
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <FaStar 
                              key={star}
                              className={`w-3 h-3 ${
                                star <= (candidate.ratings?.overall || 0)
                                  ? 'text-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {candidate.appliedDate ? new Date(candidate.appliedDate).toLocaleDateString() : 'N/A'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className={`px-6 py-12 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <FaUsers className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-lg font-medium mb-2">No Candidates Yet</p>
                    <p>Click the + button to add your first candidate.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Render notes tab
  const renderNotesTab = () => (
    <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 w-full max-w-full overflow-hidden`}>
      <div className="space-y-6 w-full">
        {/* Notes Header with Add Button */}
        <div className="flex justify-between items-center">
          <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
            Notes & Updates
          </h3>
          <button
            onClick={() => setShowAddNoteModal(true)}
            className={`p-2 text-white ${darkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-600 hover:bg-red-700'} rounded-full transition-colors duration-200`}
          >
            <FaPlus className="h-6 w-6" />
          </button>
        </div>

        {/* Notes List */}
        <div className="space-y-4">
          {notes?.length > 0 ? (
            notes.map((note, index) => (
              <div key={note.id || index} className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} rounded-lg p-4 w-full max-w-full border`}>
                <div className="flex items-start justify-between w-full">
                  <div className="flex items-start space-x-3 flex-1 min-w-0 max-w-full">
                    <div className={`w-8 h-8 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'} rounded-full flex items-center justify-center overflow-hidden flex-shrink-0`}>
                      {note.author?.photoURL ? (
                        <img 
                          src={note.author.photoURL} 
                          alt={note.author.name}
                          className="w-full h-full object-cover"
                        />
                      ) : note.author?.name === 'System' ? (
                        <img 
                          src="/systemicon.png" 
                          alt="System"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {note.author?.name?.split(' ').map(n => n[0]).join('') || 'U'}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 max-w-full overflow-hidden">
                      <div className="flex items-center space-x-2">
                        <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {note.author?.name || 'Unknown User'}
                        </span>
                        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {note.date || new Date(note.timestamp).toLocaleString()}
                        </span>
                        {note.editedAt && (
                          <span className={`text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            (edited)
                          </span>
                        )}
                      </div>
                      {editingNoteId === note.id ? (
                        <div className="mt-2 space-y-2">
                          <textarea
                            className={`w-full p-2 border rounded-lg text-sm focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            rows="3"
                            value={editingNoteText}
                            onChange={(e) => setEditingNoteText(e.target.value)}
                          />
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={handleCancelEditNote}
                              className={`px-3 py-1 text-sm rounded ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleSaveEditNote(note.id)}
                              className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} whitespace-pre-wrap mt-1 break-words overflow-wrap-anywhere`}>
                          {note.content}
                        </p>
                      )}
                    </div>
                  </div>
                  {editingNoteId !== note.id && (
                    <div className="flex items-center space-x-1 ml-2 shrink-0">
                      <button
                        onClick={() => handleStartEditNote(note)}
                        className={`${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}
                        title="Edit note"
                      >
                        <FaEdit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className={`${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-700'}`}
                        title="Delete note"
                      >
                        <FaTimes className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <p>No notes yet. Add your first note to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative text-center">
          <BBALoader size={48} className="mx-auto" />
          <p className={`mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading position request...</p>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative text-center">
          <FaExclamationTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Request Not Found</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">The position request you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/crm/fulfillment/hr')}
            className="bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-600 transition-colors"
          >
            Back to HR Fulfillment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-6 relative ${darkMode ? 'text-white' : 'text-gray-900'}`}>
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 relative">
          <div className="flex-1 flex items-center">
            <button
              onClick={() => navigate('/crm/fulfillment/hr')}
              className={`flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
                darkMode 
                  ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
              title="Back to HR Fulfillment"
            >
              <FaArrowLeft className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center absolute left-1/2 transform -translate-x-1/2">
            {editing ? (
              <input
                type="text"
                value={editData.position || ''}
                onChange={(e) => setEditData({ ...editData, position: e.target.value })}
                placeholder="Position title"
                className={`text-3xl font-bold text-center bg-transparent border-b-2 border-red-500 focus:outline-none px-2 ${darkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
              />
            ) : (
              <h1 className={`text-3xl font-bold tracking-tight text-center text-gray-900 dark:text-white`}>
                {request.position}
              </h1>
            )}
            <div className="ml-4 relative status-dropdown-container">
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className="focus:outline-none"
              >
                {getStatusBadge(pendingStatus || request.status)}
              </button>
              
              {showStatusDropdown && (
                <div className={`absolute top-full left-0 mt-2 w-48 rounded-md shadow-lg z-50 ${
                  darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                }`}>
                  <div className="py-1">
                    <button
                      onClick={() => handleStatusUpdate('in-progress')}
                      className={`w-full text-left px-4 py-2 text-sm ${
                        (pendingStatus || request.status) === 'in-progress'
                          ? (darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900')
                          : (darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100')
                      }`}
                    >
                      <div className="flex items-center">
                        <FaUsers className="w-4 h-4 mr-2" />
                        In Progress
                        {pendingStatus === 'in-progress' && (
                          <span className="ml-2 text-xs text-blue-500">(Selected)</span>
                        )}
                      </div>
                    </button>
                    <button
                      onClick={() => handleStatusUpdate('completed')}
                      className={`w-full text-left px-4 py-2 text-sm ${
                        (pendingStatus || request.status) === 'completed'
                          ? (darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900')
                          : (darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100')
                      }`}
                    >
                      <div className="flex items-center">
                        <FaCheckCircle className="w-4 h-4 mr-2" />
                        Completed
                        {pendingStatus === 'completed' && (
                          <span className="ml-2 text-xs text-blue-500">(Selected)</span>
                        )}
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 flex justify-end">
            <div className="flex space-x-2">
              {hasUnsavedStatusChange ? (
                <div className="flex space-x-2">
                  <button
                    onClick={handleCancelStatusChange}
                    className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                  >
                    <FaTimes className="w-4 h-4 mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveStatusChange}
                    disabled={isSavingStatus}
                    className={`flex items-center px-4 py-2 rounded-md transition-colors ${
                      isSavingStatus 
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                        : 'bg-green-500 text-white hover:bg-green-600'
                    }`}
                  >
                    {isSavingStatus ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="w-4 h-4 mr-2" />
                        Save Status
                      </>
                    )}
                  </button>
                </div>
              ) : !editing ? (
                <button
                  onClick={() => setEditing(true)}
                  className={`flex items-center px-4 py-2 rounded-md transition-colors ${
                    darkMode 
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <FaEdit className="w-4 h-4 mr-2" />
                  Edit
                </button>
              ) : (
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="flex items-center px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                  >
                    <FaTimes className="w-4 h-4 mr-2" />
                    Delete
                  </button>
                  <button
                    onClick={handleCancel}
                    className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                  >
                    <FaTimes className="w-4 h-4 mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex items-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                  >
                    <FaSave className="w-4 h-4 mr-2" />
                    Save
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>


        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Requested By — combined company + main contact, with avatars */}
          <div className={`md:col-span-2 p-6 rounded-lg shadow-md border ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="flex items-center gap-4">
              <div className="shrink-0">
                <HiringAvatarCluster
                  account={requestAccount}
                  company={request.companyName}
                  teamMembers={accountTeam}
                  showNames={false}
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Requested By
                </p>
                <p className={`text-2xl font-bold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {request.companyName}
                </p>
                <p className={`text-base font-medium truncate ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  {request.mainContactName || 'Unknown Contact'}
                </p>
              </div>
              {requestAccount?.id && (
                <button
                  onClick={() => navigate(`/crm/client/${requestAccount.id}`)}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    darkMode
                      ? 'bg-gray-700 text-blue-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-blue-600 hover:bg-gray-200'
                  }`}
                  title="Open client overview"
                >
                  Client Overview
                  <FaExternalLinkAlt className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          <div
            className={`p-6 rounded-lg shadow-md border cursor-pointer hover:shadow-lg transition-shadow duration-200 ${
              darkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-750' : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => {
              setNewBudget(request.budget || '');
              setShowBudgetModal(true);
            }}
          >
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${
                darkMode ? 'bg-green-900' : 'bg-green-100'
              }`}>
                <FaDollarSign className={`text-2xl ${darkMode ? 'text-green-200' : 'text-green-600'}`} />
              </div>
              <div className="ml-4">
                <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Budget
                </p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {request.budget || 'Click to set'}
                </p>
              </div>
            </div>
          </div>

          <div className={`p-6 rounded-lg shadow-md border ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${
                darkMode ? 'bg-yellow-900' : 'bg-yellow-100'
              }`}>
                <FaClock className={`text-2xl ${darkMode ? 'text-yellow-200' : 'text-yellow-600'}`} />
              </div>
              <div className="ml-4">
                <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Request Date
                </p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {formatDate(request.requestDate).split(',')[0]}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} mb-6`}>
          <nav className="-mb-px flex justify-center space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? `border-red-500 ${darkMode ? 'text-red-400' : 'text-red-600'}`
                    : `border-transparent ${
                        darkMode 
                          ? 'text-gray-400 hover:text-gray-300 hover:border-gray-600' 
                          : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'timeline' && renderTimelineTab()}
          {activeTab === 'candidates' && renderCandidatesTab()}
          {activeTab === 'notes' && renderNotesTab()}
        </div>
      </div>

      {/* Add Note Modal */}
      {showAddNoteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-lg w-full mx-4`}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1"></div>
              <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
                Add New Note
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => {
                    setShowAddNoteModal(false);
                    setNewNote('');
                  }}
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <textarea
                className={`w-full p-3 border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                rows="4"
                placeholder="Write your note here..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAddNoteModal(false);
                    setNewNote('');
                  }}
                  className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddNote}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Add Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Candidate Modal */}
      {showAddCandidateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-2xl w-full mx-4`}>
            <div className="flex justify-between items-center mb-6">
              <div className="flex-1"></div>
              <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
                Add New Candidate
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => {
                    setShowAddCandidateModal(false);
                    setNewCandidate({
                      name: '',
                      status: 'applied',
                      experience: '',
                      score: '',
                      appliedDate: '',
                      nextStep: ''
                    });
                  }}
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Candidate Name *
                  </label>
                  <input
                    type="text"
                    className={`w-full p-3 border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                    placeholder="Enter candidate name"
                    value={newCandidate.name}
                    onChange={(e) => setNewCandidate({...newCandidate, name: e.target.value})}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Applied Date
                  </label>
                  <input
                    type="date"
                    className={`w-full p-3 border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                    value={newCandidate.appliedDate}
                    onChange={(e) => setNewCandidate({...newCandidate, appliedDate: e.target.value})}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowAddCandidateModal(false);
                    setNewCandidate({
                      name: '',
                      status: 'applied',
                      experience: '',
                      score: '',
                      appliedDate: '',
                      nextStep: ''
                    });
                  }}
                  className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCandidate}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Add Candidate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Candidate Details Modal */}
      {showCandidateModal && selectedCandidate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 overflow-y-auto">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-6xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto`}>
            <div className="flex justify-between items-center mb-6 gap-3">
              <div className="flex-1 flex justify-start">
                <button
                  onClick={() => {
                    setShowCandidateModal(false);
                    setSelectedCandidate(null);
                    setEditingCandidate(false);
                    setResumeFile(null);
                    setCandidateEditData({});
                    setUploadingResume(false);
                    setUploadProgress(0);
                  }}
                  className={`p-2 rounded-full transition-colors ${
                    darkMode
                      ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  title="Close"
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
              <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} text-center`}>
                {selectedCandidate.name}
              </h3>
              <div className="flex-1 flex justify-end items-center gap-2">
                <button
                  onClick={() => handleActionSelect('reject')}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedAction === 'reject'
                      ? 'bg-red-600 text-white'
                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                  }`}
                  title="Reject candidate"
                >
                  <FaTimes className="w-4 h-4 mr-1.5" />
                  Reject
                </button>
                <button
                  onClick={() => handleActionSelect('continue')}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedAction === 'continue'
                      ? 'bg-green-600 text-white'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                  title="Continue candidate"
                >
                  <FaCheckCircle className="w-4 h-4 mr-1.5" />
                  Continue
                </button>
                <button
                  onClick={() => handleToggleFavorite(selectedCandidate.id)}
                  className={`p-2 transition-colors duration-200 ${
                    selectedCandidate.isFavorite
                      ? 'text-red-500 hover:text-red-600'
                      : (darkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-500 hover:text-red-500')
                  }`}
                  title={selectedCandidate.isFavorite ? "Remove from Favorites" : "Mark as Favorite"}
                >
                  <FaHeart className={`h-5 w-5 ${selectedCandidate.isFavorite ? 'fill-current' : ''}`} />
                </button>
              </div>
            </div>
            
            <div className="space-y-6">


              {/* Basic Information (wide) and Candidate Ratings (slim sidebar) */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Basic Information */}
                <div className={`lg:col-span-2 p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
                  <h5 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Basic Information
                  </h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`text-sm font-medium text-center block ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        Full Name
                      </label>
                      {editingCandidate ? (
                        <input
                          type="text"
                          className={`w-full p-2 text-center border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-900'}`}
                          value={candidateEditData.name}
                          onChange={(e) => setCandidateEditData({...candidateEditData, name: e.target.value})}
                        />
                      ) : (
                        <p className={`text-sm text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {selectedCandidate.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className={`text-sm font-medium text-center block ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        Applied Date
                      </label>
                      {editingCandidate ? (
                        <input
                          type="date"
                          className={`w-full p-2 text-center border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-900'}`}
                          value={candidateEditData.appliedDate}
                          onChange={(e) => setCandidateEditData({...candidateEditData, appliedDate: e.target.value})}
                        />
                      ) : (
                        <p className={`text-sm text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {selectedCandidate.appliedDate ? new Date(selectedCandidate.appliedDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }) : 'N/A'}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className={`text-sm font-medium text-center block ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        Email
                      </label>
                      {editingCandidate ? (
                        <input
                          type="email"
                          className={`w-full p-2 text-center border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-900'}`}
                          value={candidateEditData.email}
                          onChange={(e) => setCandidateEditData({...candidateEditData, email: e.target.value})}
                          placeholder="candidate@email.com"
                        />
                      ) : (
                        <p className={`text-sm text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {selectedCandidate.email || 'Not specified'}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className={`text-sm font-medium text-center block ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        Phone
                      </label>
                      {editingCandidate ? (
                        <input
                          type="tel"
                          className={`w-full p-2 text-center border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-900'}`}
                          value={candidateEditData.phone}
                          onChange={(e) => setCandidateEditData({...candidateEditData, phone: e.target.value})}
                          placeholder="(555) 123-4567"
                        />
                      ) : (
                        <p className={`text-sm text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {selectedCandidate.phone || 'Not specified'}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Resume Section */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                    <div className="text-center mb-4">
                      <h6 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Resume
                      </h6>
                    </div>
                    {editingCandidate ? (
                      <div className={`p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                        <div className="text-center">
                          {uploadingResume ? (
                            <div className="space-y-3">
                              <div className="flex items-center justify-center">
                                <BBALoader size={24} />
                                <span className={`ml-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                  Uploading... {uploadProgress}%
                                </span>
                              </div>
                              <div className={`w-full bg-gray-200 rounded-full h-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                <div 
                                  className="bg-red-500 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${uploadProgress}%` }}
                                ></div>
                              </div>
                            </div>
                          ) : (
                            <>
                              <input
                                type="file"
                                accept=".pdf"
                                onChange={handleResumeUpload}
                                className="hidden"
                                id="resume-upload"
                                disabled={uploadingResume}
                              />
                              <label
                                htmlFor="resume-upload"
                                className={`flex items-center justify-center px-4 py-2 ${darkMode ? 'bg-gray-500 hover:bg-gray-400' : 'bg-gray-300 hover:bg-gray-400'} rounded-lg transition-colors duration-200 cursor-pointer ${uploadingResume ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                <FaUpload className="w-4 h-4 mr-2" />
                                {resumeFile ? resumeFile.name : 'Upload Resume PDF'}
                              </label>
                              {resumeFile && !uploadingResume && (
                                <p className={`text-xs mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                  Selected: {resumeFile.name}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className={`p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                        {selectedCandidate.resumeUrl ? (
                          <div className="flex items-center justify-center gap-2">
                            <a
                              href={selectedCandidate.resumeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center px-3 py-2 ${darkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-gray-300 hover:bg-gray-400 text-gray-800'} rounded-lg transition-colors duration-200`}
                              title="View resume in a new tab"
                            >
                              <FaExternalLinkAlt className="w-4 h-4 mr-2" />
                              View
                            </a>
                            <a
                              href={selectedCandidate.resumeUrl}
                              download={selectedCandidate.resumeFileName || `${selectedCandidate.name}_Resume.pdf`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center px-3 py-2 ${darkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-gray-300 hover:bg-gray-400 text-gray-800'} rounded-lg transition-colors duration-200`}
                              title="Download resume"
                            >
                              <FaDownload className="w-4 h-4 mr-2" />
                              Download
                            </a>
                            <button
                              type="button"
                              onClick={handleDeleteResume}
                              className="flex items-center px-3 py-2 bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-300 dark:hover:bg-red-900/60 rounded-lg transition-colors duration-200"
                              title="Delete resume"
                            >
                              <FaTrash className="w-4 h-4 mr-2" />
                              Delete
                            </button>
                          </div>
                        ) : (
                          <div className="text-center">
                            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              No resume uploaded
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Star Ratings — Overall and Culture stacked as separate cards */}
                <div className="lg:col-span-1 space-y-6">
                  <div className={`p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
                    <h5 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Overall
                    </h5>
                    <div className="flex justify-center">
                      {renderStarRating('overall', candidateRatings.overall)}
                    </div>
                  </div>
                  <div className={`p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
                    <h5 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Culture
                    </h5>
                    <div className="flex justify-center">
                      {renderStarRating('culture', candidateRatings.culture)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Company Feedback - full width (actions moved to header) */}
              {selectedCandidate.feedbackRating && (
                <div className="grid grid-cols-1 gap-6">
                  <div className={`p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex-1"></div>
                      <h5 className={`text-xl font-semibold text-center flex-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Company Feedback
                      </h5>
                      <div className="flex-1 flex justify-end">
                        <button
                          onClick={handleOpenFeedback}
                          className={`p-2 rounded-full transition-colors duration-200 ${
                            darkMode 
                              ? 'bg-gray-600 hover:bg-gray-500 text-gray-300 hover:text-white' 
                              : 'bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800'
                          }`}
                          title="Edit Feedback"
                        >
                          <FaEdit className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="text-center mb-4">
                      <div className="flex justify-center space-x-1 mb-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <FaStar 
                            key={star}
                            className={`w-6 h-6 ${
                              star <= selectedCandidate.feedbackRating
                                ? 'text-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {selectedCandidate.feedbackRating === 1 ? 'Poor' :
                         selectedCandidate.feedbackRating === 2 ? 'Fair' :
                         selectedCandidate.feedbackRating === 3 ? 'Good' :
                         selectedCandidate.feedbackRating === 4 ? 'Very Good' : 'Excellent'}
                      </p>
                    </div>
                    {selectedCandidate.feedbackNote && (
                      <div className={`p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {selectedCandidate.feedbackNote}
                        </p>
                      </div>
                    )}
                    {selectedCandidate.feedbackUpdatedAt && (
                      <p className={`text-xs text-center mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Updated: {new Date(selectedCandidate.feedbackUpdatedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Hiring Steps and Looking For - Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Hiring Process Steps */}
                <div className={`p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
                  <h5 className={`text-xl font-semibold mb-1 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Hiring Process Steps
                  </h5>
                  <p className={`text-xs mb-6 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Check off each step as this candidate moves through the process.
                  </p>
                  <div className="space-y-2">
                    {(() => {
                      const candidateProcessStepData = [
                        {
                          id: 'applied',
                          title: 'Applied',
                          description: 'Candidate submitted application',
                          defaultCompleted: true,
                          defaultDate: selectedCandidate.appliedDate
                        },
                        {
                          id: 'pre-screen-interview',
                          title: 'Pre-Screen Interview',
                          description: 'Initial phone or video interview completed',
                          statusDependent: false
                        },
                        {
                          id: 'company-interview',
                          title: 'Company Interview',
                          description: 'In-person or virtual interview with team members',
                          statusDependent: false
                        },
                        {
                          id: 'decision-offer',
                          title: 'Decision & Offer',
                          description: 'Final evaluation and job offer decision',
                          statusDependent: false
                        }
                      ];

                      const nextStepIndex = candidateProcessStepData.findIndex((step) => {
                        const sd = candidateProcessSteps[step.id];
                        return !(sd?.completed || (step.defaultCompleted && !sd));
                      });

                      return candidateProcessStepData.map((step, index) => {
                        const stepData = candidateProcessSteps[step.id];
                        const isCompleted = stepData?.completed || (step.defaultCompleted && !stepData);
                        const completionDate = stepData?.completedDate || (step.defaultDate && !stepData ? step.defaultDate : null);
                        const isClickable = !step.statusDependent;
                        const isCurrent = index === nextStepIndex;

                        return (
                          <div
                            key={step.id}
                            className={`flex items-start space-x-4 rounded-lg p-3 transition-colors ${
                              isCurrent
                                ? (darkMode ? 'bg-red-900/20 ring-2 ring-red-500' : 'bg-red-50 ring-2 ring-red-500')
                                : ''
                            }`}
                          >
                            <div className="flex-shrink-0">
                              <button
                                type="button"
                                onClick={isClickable ? () => handleCandidateProcessStepClick(step.id) : undefined}
                                disabled={!isClickable}
                                title={isCompleted ? 'Click to mark as incomplete' : 'Click to mark this step complete'}
                                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-500 ${
                                  darkMode ? 'focus-visible:ring-offset-gray-700' : 'focus-visible:ring-offset-white'
                                } ${isClickable ? 'cursor-pointer' : 'cursor-default'} ${
                                  isCompleted
                                    ? 'text-green-500 hover:text-green-600'
                                    : isCurrent
                                    ? (darkMode ? 'text-red-400 hover:text-green-500' : 'text-red-500 hover:text-green-600')
                                    : (darkMode ? 'text-gray-500 hover:text-green-500' : 'text-gray-400 hover:text-green-600')
                                }`}
                              >
                                {isCompleted ? (
                                  <FaCheckCircle className="w-7 h-7" />
                                ) : (
                                  <FaRegCircle className="w-7 h-7" />
                                )}
                              </button>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center flex-wrap gap-2 mb-1">
                                <span className={`text-sm font-medium ${
                                  isCompleted
                                    ? (darkMode ? 'text-green-200' : 'text-green-800')
                                    : isCurrent
                                    ? (darkMode ? 'text-red-300' : 'text-red-700')
                                    : (darkMode ? 'text-white' : 'text-gray-900')
                                }`}>
                                  {step.title}
                                </span>
                                {isCurrent && (
                                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-600 text-white">
                                    Next — check off when done
                                  </span>
                                )}
                                {isCompleted && completionDate && (
                                  <span className={`text-xs ${darkMode ? 'text-green-300' : 'text-green-600'}`}>
                                    {formatDate(completionDate)}
                                  </span>
                                )}
                                {isCompleted && stepData && (
                                  <button
                                    onClick={() => handleMarkCandidateStepIncomplete(step.id)}
                                    className={`text-xs px-2 py-1 rounded ${
                                      darkMode
                                        ? 'text-red-300 hover:bg-red-900'
                                        : 'text-red-600 hover:bg-red-100'
                                    } transition-colors`}
                                    title="Mark as incomplete"
                                  >
                                    Undo
                                  </button>
                                )}
                              </div>
                              <p className={`text-sm ${
                                isCompleted
                                  ? (darkMode ? 'text-green-200' : 'text-green-700')
                                  : (darkMode ? 'text-gray-400' : 'text-gray-600')
                              }`}>
                                {step.description}
                              </p>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* Looking For */}
                <div className={`p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
                  <h5 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Looking For
                  </h5>
                  
                  {/* Salary and Start Date Row */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className={`text-sm font-medium text-center block ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        Salary
                      </label>
                      {editingCandidate ? (
                        <input
                          type="text"
                          className={`w-full p-2 text-center border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-900'}`}
                          value={candidateEditData.salary}
                          onChange={(e) => setCandidateEditData({...candidateEditData, salary: e.target.value})}
                          placeholder="e.g., $50,000 - $70,000"
                        />
                      ) : (
                        <p className={`text-sm text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {selectedCandidate.salary || 'Not specified'}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className={`text-sm font-medium text-center block ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        Start Date
                      </label>
                      {editingCandidate ? (
                        <input
                          type="date"
                          className={`w-full p-2 text-center border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-900'}`}
                          value={candidateEditData.startDate}
                          onChange={(e) => setCandidateEditData({...candidateEditData, startDate: e.target.value})}
                        />
                      ) : (
                        <p className={`text-sm text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {selectedCandidate.startDate || 'Not specified'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Additional Items */}
                  <div>
                    <label className={`text-sm font-medium text-center block mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      Additional Items
                    </label>
                    {editingCandidate ? (
                      <textarea
                        className={`w-full p-3 border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-900'}`}
                        rows="3"
                        value={candidateEditData.additionalItems}
                        onChange={(e) => setCandidateEditData({...candidateEditData, additionalItems: e.target.value})}
                        placeholder="Enter any additional items or requirements..."
                      />
                    ) : (
                      <div className={`p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                        <p className={`text-sm text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {selectedCandidate.additionalItems || 'No additional items specified'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons — sticky footer so it's always visible */}
              <div className={`sticky bottom-0 z-10 -mx-6 -mb-6 px-6 py-4 flex justify-between items-center border-t ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                {/* Left: destructive delete, only while editing */}
                <div>
                  {editingCandidate && (
                    <button
                      onClick={handleDeleteCandidate}
                      className="inline-flex items-center px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors duration-200"
                    >
                      <FaTrash className="w-4 h-4 mr-2" />
                      Delete
                    </button>
                  )}
                </div>
                {/* Right: cancel / save (editing) or edit (viewing) */}
                <div className="flex space-x-3">
                  {editingCandidate ? (
                    <>
                      <button
                        onClick={handleCancelCandidateEdit}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveCandidateEdit}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                      >
                        Save Changes
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleStartEditCandidate}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                    >
                      Edit Candidate
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timeline Step Completion Modal */}
      {showTimelineModal && selectedStep && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4`}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1"></div>
              <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
                Mark Step Complete
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => {
                    setShowTimelineModal(false);
                    setSelectedStep(null);
                    setStepCompletionDate('');
                  }}
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Completion Date *
                </label>
                <input
                  type="date"
                  className={`w-full p-3 border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                  value={stepCompletionDate}
                  onChange={(e) => setStepCompletionDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowTimelineModal(false);
                    setSelectedStep(null);
                    setStepCompletionDate('');
                  }}
                  className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleMarkStepComplete}
                  disabled={!stepCompletionDate}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    stepCompletionDate
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  }`}
                >
                  Mark Complete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Candidate Process Step Completion Modal */}
      {showCandidateProcessModal && selectedCandidateStep && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4`}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1"></div>
              <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
                Mark Process Step Complete
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => {
                    setShowCandidateProcessModal(false);
                    setSelectedCandidateStep(null);
                    setCandidateStepCompletionDate('');
                  }}
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Completion Date *
                </label>
                <input
                  type="date"
                  className={`w-full p-3 border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                  value={candidateStepCompletionDate}
                  onChange={(e) => setCandidateStepCompletionDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCandidateProcessModal(false);
                    setSelectedCandidateStep(null);
                    setCandidateStepCompletionDate('');
                  }}
                  className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleMarkCandidateStepComplete}
                  disabled={!candidateStepCompletionDate}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    candidateStepCompletionDate
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  }`}
                >
                  Mark Complete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Budget Edit Modal */}
      {showBudgetModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4`}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1"></div>
              <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
                Update Budget
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => {
                    setShowBudgetModal(false);
                    setNewBudget('');
                  }}
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Budget Amount
                </label>
                <input
                  type="text"
                  className={`w-full p-3 border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                  placeholder="e.g., $50,000 - $70,000"
                  value={newBudget}
                  onChange={(e) => setNewBudget(e.target.value)}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowBudgetModal(false);
                    setNewBudget('');
                  }}
                  className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBudgetUpdate}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Update Budget
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Description Modal */}
      {showEditDescriptionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-2xl w-full mx-4`}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1"></div>
              <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
                Edit Posting Requirements
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={handleCancelEditDescription}
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Posting Requirements
                </label>
                <textarea
                  className={`w-full p-3 border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                  rows="8"
                  placeholder="Enter posting requirements..."
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelEditDescription}
                  className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveDescription}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4`}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1"></div>
              <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
                Delete Position Request
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <p className={`text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Are you sure you want to delete this position request? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteRequest}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && selectedCandidate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-2xl w-full mx-4`}>
            <div className="flex justify-between items-center mb-6">
              <div className="flex-1"></div>
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
                Company Rating - {selectedCandidate.name}
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={handleCancelFeedback}
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="space-y-6">
              {/* Star Rating */}
              <div className="text-center">
                <h4 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Rate this candidate (1-5 stars)
                </h4>
                <div className="flex justify-center space-x-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setFeedbackRating(star)}
                      className={`transition-colors duration-200 ${
                        star <= feedbackRating 
                          ? 'text-yellow-400 hover:text-yellow-500' 
                          : 'text-gray-300 hover:text-yellow-400'
                      }`}
                    >
                      <FaStar className="w-8 h-8" />
                    </button>
                  ))}
                </div>
                <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {feedbackRating === 0 ? 'No rating selected' : 
                   feedbackRating === 1 ? 'Poor' :
                   feedbackRating === 2 ? 'Fair' :
                   feedbackRating === 3 ? 'Good' :
                   feedbackRating === 4 ? 'Very Good' : 'Excellent'}
                </p>
              </div>

              {/* Feedback Note */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Feedback Note
                </label>
                <textarea
                  className={`w-full p-3 border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                  rows="4"
                  placeholder="Write your feedback about this candidate..."
                  value={feedbackNote}
                  onChange={(e) => setFeedbackNote(e.target.value)}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelFeedback}
                  className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveFeedback}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PositionRequestOverview;
